import { computed, inject } from '@angular/core';
import { PreferencesStore, RouterStore } from '@ct/data';
import { CaptureFilter, ColumnFilters, getDefaultFilter, sanitizeAndFormatDate, sanitizeDate } from '@ct/model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

interface FiltersState {
  filters: CaptureFilter | null;
  activePreference: string;
  columnFilters: ColumnFilters | null;
  visibleColumnFilters: string[];
  highlightUpdated: string;
  onlyUnmatched: boolean;
  onlyLocalUpdates: boolean;
  onlyDeleted: boolean;
  freeText: string;
  filtersChanged: boolean;
}

const initialState: FiltersState = {
  filters: null,
  activePreference: '',
  columnFilters: null,
  visibleColumnFilters: [],
  freeText: '',
  highlightUpdated: '24h',
  onlyUnmatched: false,
  onlyLocalUpdates: false,
  onlyDeleted: false,
  filtersChanged: false,
};

export const FiltersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed((store) => {
    const routerStore = inject(RouterStore);
    const preferenceStore = inject(PreferencesStore);

    // Get dependencies from global store
    const queryParams = routerStore.queryParams;
    const filterPreferences = preferenceStore.filterPreferences;
    const columnSettings = preferenceStore.columnSettings;

    return {
      // Computed capture filters with fallback logic
      captureFilters: computed(() => {
        const filters = store.filters();
        const params = queryParams();
        const preferences = filterPreferences();

        if (filters) {
          return filters;
        }

        // Get filter from URL parameters
        if (params && Object.keys(params).length) {
          return {
            dateDestination: {
              start: sanitizeDate(params.dateDestinationStart),
              end: sanitizeDate(params.dateDestinationEnd),
            },
            bldate: {
              start: sanitizeDate(params.blDateStart),
              end: sanitizeDate(params.blDateEnd),
            },
            regionIds: params.regionIds ? params.regionIds.split(',') : [],
          };
        }

        // If no filter preferences, use localStorage
        if (!Object.values(preferences).length) {
          const stored = localStorage.getItem('lngFilterSettings');
          if (stored) {
            try {
              return JSON.parse(stored) as CaptureFilter;
            } catch {
              return getDefaultFilter();
            }
          }
        }

        return getDefaultFilter();
      }),

      // Computed current preference
      currentPreference: computed(() => {
        const activePreference = store.activePreference();
        const params = queryParams();
        const preferences = filterPreferences();

        if (activePreference) {
          return activePreference;
        }

        if (params && Object.keys(params).length) {
          return params.filterId || '';
        }

        // Get default filter
        const defaultFilter = Object.values(preferences).find(
          (pref) => pref && typeof pref === 'object' && 'default' in pref && pref.default,
        );
        if (defaultFilter && typeof defaultFilter === 'object' && 'id' in defaultFilter) {
          return defaultFilter.id as string;
        }

        // Get last used filter from localStorage
        const filterId = localStorage.getItem('lngActiveFilterId');
        return filterId || '';
      }),

      // Computed column filters (cleaned)
      cleanedColumnFilters: computed(() => {
        const columnFilters = store.columnFilters();
        if (!columnFilters) return {};

        const cleaned: ColumnFilters = {};
        Object.keys(columnFilters).forEach((colProp) => {
          if (columnFilters[colProp]?.length > 0) {
            cleaned[colProp] = columnFilters[colProp];
          }
        });
        return cleaned;
      }),

      // Computed column filter settings
      columnFilterSettings: computed(() => {
        const settings = columnSettings();
        const visibleColFilters = store.visibleColumnFilters();

        return settings.map((col) => ({
          ...col,
          filterVisible: visibleColFilters.includes(col.prop),
        }));
      }),
    };
  }),

  withMethods((store) => {
    const routerStore = inject(RouterStore);

    return {
      // Set capture filter
      setCaptureFilter: (filters: CaptureFilter) => {
        localStorage.setItem('lngFilterSettings', JSON.stringify(filters));
        patchState(store, { filters, onlyDeleted: false });

        // Update router with filter parameters
        const currentPreference = store.currentPreference();
        routerStore.navigate({
          commands: [],
          extras: {
            queryParams: {
              filterId: currentPreference || '',
              blDateStart: sanitizeAndFormatDate(filters?.bldate.start),
              blDateEnd: sanitizeAndFormatDate(filters?.bldate.end),
              dateDestinationStart: sanitizeAndFormatDate(filters?.dateDestination.start),
              dateDestinationEnd: sanitizeAndFormatDate(filters?.dateDestination.end),
              regionIds: filters?.regionIds ? filters.regionIds.toString() : '',
            },
          },
        });
      },

      // Select filter preference
      selectFilterPreference: (id: string) => {
        localStorage.setItem('lngActiveFilterId', id);
        patchState(store, {
          activePreference: id,
          filtersChanged: false,
        });
      },

      // Set free text filter
      setFreeText: (text: string) => {
        patchState(store, { freeText: text });
      },

      // Set column filters
      setColumnFilters: (columnFilters: ColumnFilters) => {
        const cleaned: ColumnFilters = {};
        Object.keys(columnFilters).forEach((colProp) => {
          if (columnFilters[colProp]?.length > 0) {
            cleaned[colProp] = columnFilters[colProp];
          }
        });

        const currentVisible = store.visibleColumnFilters();
        const visibleColumnFilters = Array.from(new Set([...currentVisible, ...Object.keys(cleaned)]));

        patchState(store, {
          columnFilters: cleaned,
          visibleColumnFilters,
          filtersChanged: !!store.columnFilters(),
        });
      },

      // Set highlight option
      setHighlightUpdated: (option: string) => {
        patchState(store, { highlightUpdated: option });
      },

      // Toggle show only unmatched
      toggleShowOnlyUnmatched: (show: boolean) => {
        patchState(store, { onlyUnmatched: show });
      },

      // Toggle show only local updates
      toggleShowOnlyLocalUpdates: (show: boolean) => {
        patchState(store, { onlyLocalUpdates: show });
      },

      // Set visible column filters
      setVisibleColumnFilters: (visibleColumnFilters: string[]) => {
        patchState(store, { visibleColumnFilters });
      },

      // Toggle only deleted
      toggleOnlyDeleted: () => {
        const current = store.onlyDeleted();
        patchState(store, { onlyDeleted: !current });
      },

      // Set filters changed status
      setFiltersChanged: (changed: boolean) => {
        patchState(store, { filtersChanged: changed });
      },

      // Handle preference success - called when preferences are loaded
      handlePreferenceSuccess: (preferences: unknown[]) => {
        // Only process if we're on the capture page
        if (!/capture/gi.test(routerStore.url())) {
          return;
        }

        const currentPreference = store.currentPreference();
        const preference = preferences?.find(
          (p) => p && typeof p === 'object' && 'id' in p && p.id === currentPreference,
        );

        // Set column filters from preference
        if (preference && typeof preference === 'object' && 'payload' in preference) {
          const payload = preference.payload;
          if (payload && typeof payload === 'object' && 'columnFilter' in payload) {
            patchState(store, { columnFilters: payload.columnFilter as ColumnFilters });
          }

          // Set capture filters from preference
          if (payload && typeof payload === 'object' && 'captureFilter' in payload) {
            const currentFilters = store.captureFilters();
            patchState(store, { filters: (payload.captureFilter as CaptureFilter) || currentFilters });
          }
        }
      },

      // Handle critical change dismissal
      handleCriticalChangeDismissal: () => {
        patchState(store, { onlyDeleted: false });
      },

      // Handle edit filter preference
      handleEditFilterPreference: () => {
        patchState(store, { filtersChanged: false });
      },
    };
  }),
);
